import { z } from "zod";
/**
 * Premium portal / Client Hub experience schemas.
 *
 * These contracts sit on top of the existing portal auth, plan, vault,
 * and project read models. They cover the first writable portal slice:
 * executive briefs, topic threads, comments, client uploads, and
 * in-app notifications.
 */
// ─── Briefs ────────────────────────────────────────────────
export const PortalBriefWorkflowStatusSchema = z.enum(["draft", "internal_review", "published"]);
export const PortalBriefHealthSchema = z.enum(["strong", "steady", "attention", "critical"]);
export const PortalBriefSectionKindSchema = z.enum([
    "relationship_summary",
    "priorities",
    "open_decisions",
    "health",
    "documents_needing_action",
    "upcoming_meetings",
    "recent_updates",
    "custom",
]);
export const PortalBriefSectionSchema = z.object({
    id: z.string().uuid(),
    briefId: z.string().uuid(),
    kind: PortalBriefSectionKindSchema,
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(8000),
    sortOrder: z.number().int(),
    isGenerated: z.boolean(),
    included: z.boolean(),
    sourceRefs: z.array(z.record(z.string(), z.unknown())).default([]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const PortalBriefSchema = z.object({
    id: z.string().uuid(),
    engagementId: z.string().uuid(),
    version: z.number().int().min(1),
    status: PortalBriefWorkflowStatusSchema,
    title: z.string().min(1).max(200),
    summary: z.string().min(1).max(8000),
    health: PortalBriefHealthSchema,
    priorityItems: z.array(z.string().min(1).max(500)).default([]),
    decisionsNeeded: z.array(z.string().min(1).max(500)).default([]),
    sourceSnapshot: z.record(z.string(), z.unknown()).default({}),
    submittedForReviewAt: z.string().datetime().nullable(),
    submittedForReviewBy: z.string().email().nullable(),
    publishedAt: z.string().datetime().nullable(),
    publishedBy: z.string().email().nullable(),
    clientNotifiedAt: z.string().datetime().nullable(),
    clientNotifiedBy: z.string().email().nullable(),
    supersededAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    sections: z.array(PortalBriefSectionSchema),
});
export const PortalBriefResponseSchema = z.object({
    brief: PortalBriefSchema,
    mode: z.enum(["draft", "review", "published", "live_generated"]),
});
export const PortalBriefSectionPatchSchema = z.object({
    id: z.string().uuid().optional(),
    kind: PortalBriefSectionKindSchema,
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(8000),
    sortOrder: z.coerce.number().int(),
    included: z.boolean().default(true),
    sourceRefs: z.array(z.record(z.string(), z.unknown())).default([]),
});
export const PortalBriefPatchSchema = z
    .object({
    title: z.string().min(1).max(200),
    summary: z.string().min(1).max(8000),
    health: PortalBriefHealthSchema,
    priorityItems: z.array(z.string().min(1).max(500)).max(12),
    decisionsNeeded: z.array(z.string().min(1).max(500)).max(12),
    sections: z.array(PortalBriefSectionPatchSchema).max(12),
})
    .partial();
// ─── Threads + Messages ───────────────────────────────────
export const PortalThreadTopicKindSchema = z.enum(["finance", "delivery", "approvals", "custom"]);
export const PortalActorKindSchema = z.enum(["staff", "portal_user", "system"]);
export const PortalThreadSchema = z.object({
    id: z.string().uuid(),
    engagementId: z.string().uuid(),
    topicKind: PortalThreadTopicKindSchema,
    title: z.string().min(1).max(200),
    description: z.string().max(1000).nullable(),
    status: z.enum(["open", "closed"]),
    sourceKind: z.string().max(40).nullable(),
    sourceId: z.string().uuid().nullable(),
    latestMessageAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const PortalThreadListResponseSchema = z.object({
    threads: z.array(PortalThreadSchema),
});
export const PortalMessageSchema = z.object({
    id: z.string().uuid(),
    threadId: z.string().uuid(),
    authorKind: PortalActorKindSchema,
    authorName: z.string().min(1).max(200),
    authorEmail: z.string().email().nullable(),
    body: z.string().min(1).max(8000),
    attachments: z.array(z.record(z.string(), z.unknown())).default([]),
    archivedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const PortalMessageListResponseSchema = z.object({
    messages: z.array(PortalMessageSchema),
});
export const PortalMessageCreateSchema = z.object({
    body: z.string().trim().min(1).max(8000),
});
// ─── Comments ──────────────────────────────────────────────
export const PortalCommentTargetKindSchema = z.enum([
    "brief",
    "brief_section",
    "document",
    "project",
    "milestone",
    "inbox_item",
    "shared_document",
]);
export const PortalCommentQuerySchema = z.object({
    targetKind: PortalCommentTargetKindSchema,
    targetId: z.string().uuid(),
});
export const PortalCommentSchema = z.object({
    id: z.string().uuid(),
    engagementId: z.string().uuid(),
    targetKind: PortalCommentTargetKindSchema,
    targetId: z.string().uuid(),
    parentCommentId: z.string().uuid().nullable(),
    authorKind: PortalActorKindSchema,
    authorName: z.string().min(1).max(200),
    authorEmail: z.string().email().nullable(),
    body: z.string().min(1).max(8000),
    status: z.enum(["open", "resolved"]),
    archivedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const PortalCommentListResponseSchema = z.object({
    comments: z.array(PortalCommentSchema),
});
export const PortalCommentCreateSchema = z.object({
    targetKind: PortalCommentTargetKindSchema,
    targetId: z.string().uuid(),
    parentCommentId: z.string().uuid().optional(),
    body: z.string().trim().min(1).max(8000),
});
// ─── Portal inbox uploads ─────────────────────────────────
export const PortalInboxStatusSchema = z.enum([
    "uploading",
    "received",
    "in_review",
    "accepted",
    "clarification_requested",
    "rejected",
    "archived",
]);
export const PortalInboxItemSchema = z.object({
    id: z.string().uuid(),
    engagementId: z.string().uuid(),
    uploadedByPortalUserId: z.string().uuid().nullable(),
    uploadedByName: z.string().min(1).max(200),
    uploadedByEmail: z.string().email(),
    filename: z.string().min(1).max(500),
    blobKey: z.string().min(1).max(1000),
    mimeType: z.string().min(1).max(150),
    sizeBytes: z.number().int().min(0),
    note: z.string().max(2000).nullable(),
    status: PortalInboxStatusSchema,
    staffNotes: z.string().max(4000).nullable(),
    reviewedBy: z.string().email().nullable(),
    reviewedAt: z.string().datetime().nullable(),
    acceptedDocumentId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const PortalInboxListResponseSchema = z.object({
    items: z.array(PortalInboxItemSchema),
});
export const PortalInboxUploadRequestSchema = z.object({
    filename: z.string().trim().min(1).max(500),
    mimeType: z.string().trim().min(1).max(150),
    sizeBytes: z.coerce
        .number()
        .int()
        .min(1)
        .max(100 * 1024 * 1024),
    note: z.string().trim().max(2000).optional(),
});
export const PortalInboxUploadTokenResponseSchema = z.object({
    item: PortalInboxItemSchema,
    uploadUrl: z.string().url(),
    expiresAt: z.string().datetime(),
});
export const PortalInboxCompleteUploadSchema = z.object({
    itemId: z.string().uuid(),
    note: z.string().trim().max(2000).optional(),
});
export const PortalInboxReviewSchema = z.object({
    status: PortalInboxStatusSchema.exclude(["uploading"]),
    staffNotes: z.string().trim().max(4000).optional(),
    targetFolderId: z.string().uuid().optional(),
});
// ─── Notifications ────────────────────────────────────────
export const PortalNotificationSchema = z.object({
    id: z.string().uuid(),
    engagementId: z.string().uuid().nullable(),
    recipientPortalUserId: z.string().uuid().nullable(),
    eventKind: z.string().min(1).max(80),
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(2000),
    linkPath: z.string().min(1).max(500).nullable(),
    readAt: z.string().datetime().nullable(),
    emailStatus: z.enum(["pending", "sent", "failed", "skipped"]),
    emailSentAt: z.string().datetime().nullable(),
    emailError: z.string().max(1000).nullable(),
    createdAt: z.string().datetime(),
});
export const PortalNotificationListResponseSchema = z.object({
    notifications: z.array(PortalNotificationSchema),
});
//# sourceMappingURL=portal-experience.js.map